import type { Dictionary } from './locale';

export const en: Dictionary = {
  brand: 'SimrLabs',
  nav: {
    home: 'Home',
    vrMods: 'VR Mods',
    uevrMods: 'UEVR Mods',
    headTrackingMods: 'Head Tracking Mods',
    mods: 'Mods',
    games: 'Games',
    simrlabs: 'SimrLabs',
    underConstruction: 'Under Construction',
    youtubeChannel: 'YouTube Channel',
    youtubeCommunity: 'YouTube Community',
    mainNavigation: 'Main navigation',
    communityLinks: 'Community links',
  },
  app: {
    loading: 'Loading SimrLabs VR Mods...',
  },
  error: {
    failedToLoad: 'Failed to load page layout.',
    returnHome: 'Go Home',
  },
  mods: {
    featuredTitle: 'VR Mods',
    noResults: 'No VR mods found.',
    loadError: 'Error loading mods database.',
    moddedBy: ({ author }) => `Modded by ${author}`,
    forGame: ({ game }) => `Game: ${game}`,
    thirdPartyNotice: 'External 3rd-party link',
    categoryFilters: 'Category filters',
    sortByLabel: 'Sort by',
    sortByUpdated: 'Updated Date',
    sortByCreated: 'Created Date',
    searchPlaceholder: 'Search mods by title, game, or tags...',
    searchAriaLabel: 'Search mods',
    clearSearch: 'Clear search',
    noSearchResults: ({ query }: { query: string }) => `No mods found matching "${query}".`,
    playVideoFor: ({ title }) => `Play video for ${title}`,
    closeVideoFor: ({ title }) => `Close video and show image for ${title}`,
    profile: {
      backToMods: '← Back to Mods',
      storePage: 'Store Page',
      mediaTitle: 'Media & Previews',
      videoTab: 'Video Preview',
      imagesTab: 'Screenshots & Images',
      descriptionTitle: 'Description & Overview',
      installationGuideTitle: 'Installation Guide',
      knownIssuesTitle: 'Known Issues & Troubleshooting',
      privacyTitle: 'Privacy Policy & Notes',
      disclaimerTitle: 'Software & Usage Disclaimer',
      creditsTitle: 'Mod Credits & Attributions',
      downloadsTitle: 'Downloads / Links',
      downloadNamed: ({ name }) => name,
      galleryThumbnails: 'Image gallery thumbnails',
      scrollThumbnailsLeft: 'Scroll thumbnails left',
      scrollThumbnailsRight: 'Scroll thumbnails right',
      showImageNamed: ({ index }) => `Show image ${index}`,
      showVideoNamed: ({ index }) => `Show video ${index}`,
      videoLabel: ({ index }) => `Video ${index}`,
      videoPreviewFor: ({ title }) => `${title} Video Preview`,
      thumbnailAlt: ({ title }) => `${title} thumbnail`,
      videoPreviewAlt: ({ title, index }) => `${title} video preview ${index}`,
      imagePreviewAlt: ({ title, index }) => `${title} preview screenshot ${index}`,
      galleryThumbnailAlt: ({ index }) => `Thumbnail ${index}`,
      inPageNavTitle: 'On This Page',
      quickDownloadsTitle: 'Quick Actions',
    },
    linkTypes: {
      internal: 'Download',
      external: 'Exterior (3rd-party)',
      web: 'Web',
      github: 'GitHub',
      community: 'Community',
      discord: 'Discord',
      other: 'Other',
    },
  },
  privacy: {
    title: 'Privacy Policy & Software Disclaimer',
    lastUpdated: 'Last Updated: September 3, 2026',
    zeroDataTitle: 'Zero Personal Data Collection',
    zeroDataDesc:
      'We do not collect, store, track, sell, or process any personal user data. You can browse this site and download files without creating an account.',
    localStorageTitle: 'Local Browser Storage',
    localStorageDesc:
      'Our web application may use browser localStorage strictly to save local settings or preferences directly on your device.',
    disclaimerTitle: 'Software Disclaimer — "Use and Download At Your Own Risk"',
    disclaimerBox:
      'ALL FILES, SOFTWARE BUILDS, AND CONTENT ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND.',
    disclaimerItem1:
      'Downloading, installing, and using any files or software from this site is strictly at your own risk.',
    disclaimerItem2:
      'The developers shall not be liable for hardware/software damage, data loss, device issues, or VR motion discomfort.',
    aiContentTitle: 'AI-Generated Content & Media Disclosure',
    aiContentDesc:
      'Certain images, card thumbnails, and promotional media on this site are generated using Artificial Intelligence (AI) for illustrative and layout demonstration purposes. In addition, original simulation games by SimrLabs utilize AI assistance for procedural generation, concept art, and engineering workflows alongside rigorous human engineering. All original third-party game titles and trademarks remain the property of their respective owners.',
  },
  games: {
    backToGames: '← Back to Games',
    gameNotFound: 'Game Not Found',
    backToHome: 'Back to Home',
    loading: ({ title }: { title?: string } = {}) =>
      title ? `Loading ${title}...` : 'Loading game details...',
    loadingSubtitle: 'Preparing simulation overview, physics engine specs, and media gallery...',
    hero: {
      originalTitle: 'Original Title by SimrLabs',
      inDevelopment: 'In Active Development',
      wishlistSteam: 'Wishlist on Steam',
      watchTrailer: 'Watch Gameplay Trailer',
      joinDiscord: 'Join Community & Discord',
      requestAlpha: 'Request Alpha Playtest Access',
      alphaModalTitle: 'Join the Sail to Unknown Closed Alpha',
      alphaModalDesc:
        'Be the first to step into the VR helm. We regularly invite closed alpha testers to test hydrodynamics, VR rigging, and open-ocean exploration.',
      alphaModalEmailPlaceholder: 'Enter your email address...',
      alphaModalSubmit: 'Sign Up for Playtest',
      alphaModalSuccess: 'Thank you! You are on the alpha playtest priority list.',
      alphaModalClose: 'Close',
      vrSupportLabel: 'VR 6DOF Native & PC Desktop (Experimental)',
      flatScreenLabel: 'PC / Flatscreen Compatible',
      engineLabel: 'PixiJS v8 / Three.js Engine',
      developerLabel: 'Developer: SimrLabs',
      publisherLabel: 'Publisher: SimrLabs',
      genreLabel: 'Genre: Sailing Simulation / Open-Sea Exploration',
      releaseTargetLabel: 'Target Release: Coming Soon to PC & VR',
      platformsLabel: 'Platforms',
    },
    sectionNavLabel: 'Game profile sections',
    tabs: {
      overview: 'Overview & Vision',
      features: 'Simulation Pillars',
      media: 'Media & Gallery',
      roadmap: 'Development Roadmap',
    },
    overview: {
      aboutTitle: 'About Sail to Unknown',
      visionTitle: 'A True Ocean Simulator Born for VR Presence',
      visionSubtitle:
        'Setting sail without artificial compromises. Stand at the helm, read the waves, and carve your own path through uncharted waters.',
      pillarsTitle: 'Core Simulation Pillars',
      developerNoteTitle: 'A Word From SimrLabs',
      developerNoteAuthor: 'Lead Developer, SimrLabs',
      developerNoteText:
        '“Sail to Unknown was born from a passion for authentic nautical hydrodynamics, open-world maritime trade, and fleet progression. Built on a physical dimension-based hull displacement model with real v⁴ wave resistance, dynamic catenary anchor physics, and a living 4-tier marine ecology, we are crafting a deeply responsive oceanic world. From standalone ship vector editing to procedural city archipelagos, we are thrilled to build this voyage in open dialogue with our community.”',
      communityTitle: 'Help Shape Development',
      communityDesc:
        'Sail to Unknown is being developed in open dialogue with the simulation and VR community. Share your feedback, suggest sailing mechanics, or join our community discussions.',
    },
    pillars: {
      vrRiggingTitle: 'Tactile 6DOF VR Rigging & Helm',
      vrRiggingDesc:
        'Grab the helm wheel with two hands, haul jib sheets, crank the bronze winches, and feel physical resistance as wind fills your sails. Fully playable with VR motion controllers or desktop HOTAS / mouse & keyboard.',
      hydrodynamicsTitle: 'Realistic Ocean Wave Physics',
      hydrodynamicsDesc:
        'Powered by continuous buoyancy and wave displacement simulation. Every crest, trough, and swell rolls the hull authentically, rewarding good seamanship and trim.',
      windTitle: 'Aerodynamic Wind & Sail Trim',
      windDesc:
        'Authentic sailing aerodynamics. Monitor telltales, apparent wind angles, and surface water ripples. Master tacking, jibing, and reefing down when squalls roll over the horizon.',
      navigationTitle: 'Celestial & Chart Navigation',
      navigationDesc:
        'No floating arcade mini-maps or GPS breadcrumbs. Unfold charts on the cabin nav table, align your magnetic compass, read the sun angle, and navigate by star constellations at night.',
    },
    vessels: {
      title: 'Vessels & Craft',
      subtitle: 'From nimble single-handers to sturdy expedition cruisers.',
      sloopName: 'The Osprey 28',
      sloopType: 'Coastal Sloop',
      sloopDesc:
        'A fast, responsive single-masted sloop ideal for shallow atolls, responsive tacking, and agile reef navigation.',
      sloopSpecSpeed: 'Max Hull Speed: 7.2 kts',
      sloopSpecCrew: 'Crew: 1 - 2 Solo / Co-op',
      sloopSpecHandling: 'Handling: High Agility',
      ketchName: 'The Wanderer 44',
      ketchType: 'Expedition Ketch',
      ketchDesc:
        'A heavily ballasted twin-masted blue-water cruiser featuring a walk-in navigational cabin, extensive cargo hold, and heavy-weather split rig.',
      ketchSpecSpeed: 'Max Hull Speed: 9.1 kts',
      ketchSpecCrew: 'Crew: 1 - 4 Expedition',
      ketchSpecHandling: 'Handling: Deep Sea Stability',
      assetsDisclaimer: 'Assets are not final and subject to change during active development.',
    },
    media: {
      title: 'Trailers & Concept Media',
      subtitle: 'In-engine captures, cockpit perspective, and ocean atmospheres.',
      allTab: 'All Media',
      videoTab: 'Videos & Gameplay',
      screenshotsTab: 'Screenshots',
      videoBadge: 'Trailer',
      openFullscreen: 'View high-resolution screenshot',
      closeFullscreen: 'Close image viewer',
      dismissViewer: 'Dismiss fullscreen image viewer',
      filterLabel: 'Media filter',
      thumbnailsLabel: 'Media thumbnails',
      videoPreviewFor: ({ title }) => `${title} Gameplay Preview Video`,
      imagePreviewAlt: ({ title, index }) => `${title} screenshot ${index}`,
      thumbnailAlt: ({ title, index }) => `${title} preview thumbnail ${index}`,
      prevMedia: 'Previous media',
      nextMedia: 'Next media',
    },
    roadmap: {
      title: 'Development Milestones',
      subtitle: 'Our journey from physics sandbox to early access release.',
      statusCompleted: 'Completed',
      statusInProgress: 'In Progress',
      statusPlanned: 'Planned',
    },
    disclaimer: {
      title: 'Development Disclaimer & AI Disclosure',
      aiDisclosureText: ({ title }) =>
        `${title} is developed with Artificial Intelligence (AI) assistance—including procedural world algorithms and generative concept media. Core simulation physics, hydrodynamics, and architecture are designed and verified by a human developer at SimrLabs. All visual assets, textures, and media represent work in progress and may change during the game development process.`,
    },
  },
  home: {
    heroTitlePart1: 'Unleash True Immersion in',
    heroTitlePart2: 'Virtual Reality',
    heroSubtitle:
      'The home for PCVR simulation mods, setup guides, and immersion tools. We test and share easy-to-use VR profiles and head tracking tweaks so you can jump into your favorite sims without the hassle.',
    exploreModsBtn: 'Explore VR Mods',
    featuredGamesBtn: 'Featured Game',
    featuredHeading: 'Featured VR Modifications',
    featuredSubtitle:
      'Hand-picked, high-performance profiles with verified 6DOF tracking and zero latency overhead.',
    viewAllMods: 'View all mods →',
    loadingFeaturedMods: 'Loading featured mods',
    gameSpotlightBadge: 'Original Title in Development',
    gameSpotlightHeading: 'Sail to Unknown',
    gameSpotlightDesc:
      'Welcome aboard! Sail to Unknown is an open-world, top-down ocean adventure where you carve your own path across the open seas. Whether you want to kick back with a fishing rod at sunset, build a trading empire across floating cities, or brave storm-tossed waters in search of uncharted islands, the ocean is yours to conquer.',
    gameSpotlightCta: 'Explore Sail to Unknown',
    ctaButton: 'Browse All Mods',
  },
  simrlabs: {
    pageTitle: 'SimrLabs In-House Creations',
    pageSubtitle:
      'Original simulation games and high-immersion VR modifications designed, built, and maintained directly by SimrLabs.',
    gamesHeading: 'Original Games',
    gamesSubtitle: 'Independent simulation experiences developed from the ground up by SimrLabs.',
    modsHeading: 'In-House VR Modifications',
    modsSubtitle:
      'Custom VR injection mods, 6DOF profiles, and immersion tools created in-house by SimrLabs.',
    exploreGameBtn: 'Explore Game Details',
    noGamesFound: 'No in-house games found.',
    noModsFound: 'No in-house VR mods found.',
    loadError: 'Failed to load SimrLabs creations.',
    loadingGames: 'Loading games',
    loadingMods: 'Loading mods',
  },
  footer: {
    copy: ({ year }) => `© ${year} SimrLabs VR Mods. All rights reserved.`,
    privacy: 'Privacy Policy & Disclaimer',
  },
  seo: {
    homeTitle: 'VR Mods & Motion Gaming Hub',
    homeDescription:
      'Discover, download, and install top-rated VR mods, UEVR game profiles, and head tracking mods with step-by-step guides at SimrLabs.',
    privacyDescription:
      'Read the privacy policy and software disclaimer for SimrLabs VR Mods. Zero data collection policy and software usage terms.',
    modProfileDescription: ({ title }) =>
      `${title} VR Mod details, installation guide, and download links at SimrLabs.`,
    gameProfileDescription: ({ title }) =>
      `${title} - An original open-ocean VR & desktop sailing simulator by SimrLabs. Experience true wind physics, hydrodynamics, and uncharted exploration.`,
    simrlabsTitle: 'SimrLabs In-House Games & VR Mods',
    simrlabsDescription:
      'Explore original simulation games and custom VR modifications built directly by SimrLabs.',
  },
};
